type getUserFullNameReturnType = { name?: string; placeholder?: string };

export type FriendsState = {
  selectedPet: {
    uid?: string;
    dateOfBirth?: string;
    name?: string;
    petType?: typeof PET_TYPE_CAT | typeof PET_TYPE_DOG;
  };
  littleFriendsFormSliderIndex: number;
  littleFriendsRegistrationModalActive: boolean;
  isLittleFriendBirthdayValid: boolean;
  littleFriendsActivated: boolean;
};

export const useFriendsStore = defineStore('friends', {
  state: (): FriendsState => ({
    selectedPet: {
      uid: undefined,
      dateOfBirth: undefined,
      name: undefined,
      petType: undefined,
    },
    littleFriendsFormSliderIndex: 0,
    littleFriendsRegistrationModalActive: false,
    isLittleFriendBirthdayValid: false,
    littleFriendsActivated: false,
  }),
  getters: {
    // TODO: Only uses state from other store, might be moved there?
    getUserFullName(): getUserFullNameReturnType {
      const userStore = useUserStore();

      const nameObj: getUserFullNameReturnType = {
        name: undefined,
        placeholder: undefined,
      };

      // Always return an object, we don't really guard against null/undefined when calling this getter
      if (!userStore.user) {
        return nameObj;
      }

      if (!userStore.user?.firstName && !userStore.user?.lastName) {
        nameObj.placeholder = 'forms.littleFriends.petSelection.placeholderName';
      } else {
        nameObj.name = `${userStore.user.firstName} ${userStore.user.lastName}`;
      }

      return nameObj;
    },

    getIsCancelDisabled(state): boolean {
      const currentIndex = state.littleFriendsFormSliderIndex;
      const petName = this.getSelectedPetName;
      const isBirthDayValid = state.isLittleFriendBirthdayValid;
      return currentIndex === 1 && isBirthDayValid && !!petName;
    },

    getSelectedPetName(state): undefined | string {
      return state.selectedPet?.name || '';
    },
  },
  actions: {
    checkLittleFriendBirthday(date: string): any {
      if (!date) {
        this.isLittleFriendBirthdayValid = false;
        return false;
      }

      const isValid = checkIfPetUnder13Months(date);

      this.isLittleFriendBirthdayValid = isValid;
      return isValid;
    },

    async patchUsername(nameObj: { name?: string; firstName: string; lastName: string }): Promise<any> {
      const userStore = useUserStore();

      const patchSuccess = await userStore.patch({
        data: nameObj,
      });

      if (patchSuccess) {
        nameObj.name = `${nameObj.firstName} ${nameObj.lastName}`;
        userStore.user = Object.assign({}, userStore.user, nameObj);
      }
    },

    async updatePet(data: Record<string, any>): Promise<any> {
      const userStore = useUserStore();
      const pet = { ...this.selectedPet, ...data };

      const patchSuccess = await userStore.updatePet(pet);
      if (patchSuccess) {
        userStore.setPet(pet);
        this.selectedPet = pet;
      }
    },

    async patchPet(data: Record<string, any>): Promise<any> {
      const { patchPet: patchUserPet, setPet } = useUserStore();
      const pet = { ...this.selectedPet };
      const uid = pet?.uid;
      const patchObj = { uid, ...data };

      const patchSuccess = await patchUserPet(patchObj);

      if (patchSuccess) {
        setPet(pet);
        this.selectedPet = { ...pet, ...data };
      }
    },

    openLittleFriendsModal(): any {
      this.littleFriendsRegistrationModalActive = true;

      // Unfortunately the Nuxt2 version of this code with nextTick doesn't work in Nuxt3
      setTimeout(() => {
        this.selectSlider();
      }, 1);
    },

    selectPreviousSlider(): any {
      this.littleFriendsFormSliderIndex = this.littleFriendsFormSliderIndex - 1;
    },

    selectSlider(): any {
      if (!this.selectedPet?.petType) {
        this.littleFriendsFormSliderIndex = 0;
        return;
      }

      if (!this.getSelectedPetName) {
        this.littleFriendsFormSliderIndex = 1;
        return;
      }

      if (this.getSelectedPetName && !this.isLittleFriendBirthdayValid) {
        this.littleFriendsFormSliderIndex = 2;
        return;
      }

      if (this.isLittleFriendBirthdayValid && this.getSelectedPetName && !this.getUserFullName.name) {
        this.littleFriendsFormSliderIndex = 3;
        return;
      }

      const { friendsStatus } = useUserStore();
      const { newsletterSubscription } = useNewsletterStore();
      const friendsState = checkFriendsState(friendsStatus);

      if (
        this.isLittleFriendBirthdayValid &&
        this.getSelectedPetName &&
        this.getUserFullName.name &&
        (!newsletterSubscription ||
          friendsState === LOYALTY_STEP.FRIENDS_AND_LITTLE_FRIENDS_INACTIVE ||
          friendsState === LOYALTY_STEP.FRIENDS)
      ) {
        this.littleFriendsFormSliderIndex = 4;
        return;
      }

      if (
        this.littleFriendsActivated ||
        (this.isLittleFriendBirthdayValid &&
          this.getSelectedPetName &&
          this.getUserFullName.name &&
          newsletterSubscription)
      ) {
        this.littleFriendsFormSliderIndex = 5;
      }
    },

    checkFriendsEligiblePets(): any {
      const userStore = useUserStore();
      const pets = userStore.getPets as Array<Record<string, any>>;

      if (!pets.length) {
        return null;
      }

      const eligibleTypes = pets.filter((pet) => pet.petType === PET_TYPE_DOG || pet.petType === PET_TYPE_CAT);

      if (!eligibleTypes.length) {
        return null;
      }

      const fullyEligibePets = eligibleTypes.filter((pet) => checkIfPetUnder13Months(pet.dateOfBirth));

      if (fullyEligibePets.length) {
        fullyEligibePets.sort(
          (petA, petB) => new Date(petB.dateOfBirth).valueOf() - new Date(petA.dateOfBirth).valueOf(),
        );
        return fullyEligibePets[0];
      }

      return eligibleTypes.filter((pet: Record<string, any>) => !pet.dateOfBirth) || null;
    },

    async fetchFriendsDonationsData(currentDate: string, period: string): Promise<any> {
      const { $api, $i18n } = useNuxtApp();
      logger.debug('Fetching friends donations...');
      const mainStore = useMainStore();

      return new Promise((resolve, reject) =>
        $api.hybris.donation
          .friends(currentDate, period, mainStore.language)
          .then(async (response: Record<string, any>) => {
            logger.debug('Friends Donations fetched.');
            return resolve(response.data);
          })
          .catch((error: any) => {
            logger.error('Error while fetching friends donations.', error);
            error.customMessage = $i18n.t('friends.donation.missingDonation');
            return reject(error);
          }),
      );
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useFriendsStore, import.meta.hot));
}
